import React, { useState, useEffect, useCallback } from 'react'
import { getSession } from '../../getSession'
import { getScheduleCallAction, sendMassAddCallListNew } from '../../utils/lead'

const AddCallList = ({ onClose = () => { }, onSave = (payload) => { }, callTypes = [], initial = {}, selectedCount = 0, registerSubmitHandler }) => {
  const [callType, setCallType] = useState(initial.callType || '')
  const [scheduleMode, setScheduleMode] = useState(initial.scheduleMode || 'next_followup')
  const [specificAt, setSpecificAt] = useState(initial.specificAt || '')
  const [anyDay, setAnyDay] = useState(initial.anyDay || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const [types, setTypes] = useState(callTypes && callTypes.length ? callTypes : [{ id: '', name: '---Select Call Type---' }])

  useEffect(() => {
    if (callTypes && callTypes.length) {
      setTypes(callTypes)
      if (!callType && callTypes[0]) setCallType(callTypes[0].id)
      return
    }

    let mounted = true
    const fetchTypes = async () => {
      try {
        const { userId, TokenId, parentId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { Mode: 'SELECT', Id: 0, SettingJSON: '', UserId: userId },
          BroadcastName: ''
        }
        const resp = await getScheduleCallAction(payload)
        let list = resp?.Details ?? resp?.data ?? resp?.d ?? resp ?? []
        if (list && list.data) list = list.data
        if (list && !Array.isArray(list)) list = list.ResultList ?? list.Items ?? list.Data ?? list.Details ?? list

        let opts = []
        if (Array.isArray(list)) {
          opts = list.map(item => {
            if (!item) return null
            const id = item.Id ?? item.id ?? item.Code ?? item.Value ?? item.AppId ?? item.AppId
            const name = item.Text ?? item.Detail ?? item.Name ?? item.DisplayName ?? item.AppName ?? item.Name ?? id
            return id ? { id: String(id), name: String(name ?? id) } : null
          }).filter(Boolean)
        } else if (list && typeof list === 'object') {
          const raw = list.Text ?? list.Detail ?? list.Code ?? ''
          try {
            const parsed = raw && typeof raw === 'string' ? JSON.parse(raw) : (raw || {})
            if (parsed && Array.isArray(parsed.leftTabs) && parsed.leftTabs.length) {
              opts = parsed.leftTabs.map(t => ({ id: String(t.name ?? t.Name ?? t.id ?? t.Id ?? t.Text ?? t.text), name: String(t.text ?? t.Text ?? t.name ?? t.Name) })).filter(Boolean)
            }
            else if (Array.isArray(parsed)) {
              opts = parsed.map(p => ({ id: String(p.id ?? p.Id ?? p.value ?? p.Value ?? p.Code ?? p), name: String(p.name ?? p.Name ?? p.text ?? p.Text ?? p.label ?? p) })).filter(Boolean)
            }
            else if (parsed && Array.isArray(parsed.Items)) {
              opts = parsed.Items.map(p => ({ id: String(p.id ?? p.Id ?? p.Value ?? p.Code ?? p.AppId ?? p.AppId), name: String(p.name ?? p.Name ?? p.Text ?? p.AppName ?? p.Name) })).filter(Boolean)
            }
            else if (parsed && (parsed.Detail || parsed.Text)) {
              const txt = parsed.Detail ?? parsed.Text ?? ''
              if (typeof txt === 'string') opts = txt.split(',').map(s => ({ id: s.trim(), name: s.trim() })).filter(o => o.name)
            }
          } catch (e) {
            if (raw && typeof raw === 'string') {
              opts = raw.split(',').map(s => ({ id: s.trim(), name: s.trim() })).filter(o => o.name)
            }
          }
        }

        if (mounted && opts.length) {
          setTypes([{ id: '', name: '---Select Call Type---' }, ...opts])
          if (!callType) setCallType(opts[0].id)
        }
      } catch (err) {
        console.warn('Failed to load schedule call action types', err?.message || err)
      }
    }

    fetchTypes()
    return () => { mounted = false }
  }, [])

  const validate = () => {
    setError('')
    if (!callType) { setError('Please select call type'); return false }
    if (scheduleMode === 'specific' && !specificAt) { setError('Choose specific time'); return false }
    if (scheduleMode === 'anyday' && !anyDay) { setError('Choose date for "Any time on this day"'); return false }
    return true
  }

  const handleAdd = useCallback(() => {
    if (!validate()) return
    const payload = { callType, scheduleMode, specificAt, anyDay, selectedCount }
    onSave(payload)
  }, [callType, scheduleMode, specificAt, anyDay, selectedCount, onSave])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleAdd)
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler, handleAdd])

  return (
    <div className="w-full max-w-lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Call List <span className='text-[#f00]'>*</span></label>
          <select value={callType} onChange={e => setCallType(e.target.value)} className="w-full border px-3 py-2 rounded" disabled={isSubmitting}>
            <option value="">---Select Call Type---</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div>
          <div className="block text-sm font-medium text-gray-700 mb-2">Schedule Time <span className='text-[#f00]'>*</span></div>
          <div className="space-y-3">
            <label className="inline-flex items-center w-full">
              <input type="radio" name="scheduleMode" value="next_followup" checked={scheduleMode === 'next_followup'} onChange={e => setScheduleMode(e.target.value)} className="mr-3" />
              <div className="text-sm">Next follow up date time</div>
            </label>

            <label className="inline-flex items-center w-full">
              <input type="radio" name="scheduleMode" value="specific" checked={scheduleMode === 'specific'} onChange={e => setScheduleMode(e.target.value)} className="mr-3" />
              <div className="text-sm">Specific time</div>
            </label>
            {scheduleMode === 'specific' && (
              <div className="pl-8">
                <input type="datetime-local" value={specificAt} onChange={e => setSpecificAt(e.target.value)} className="w-full border px-3 py-2 rounded" placeholder="Choose specific time" disabled={isSubmitting} />
              </div>
            )}

            <label className="inline-flex items-center w-full">
              <input type="radio" name="scheduleMode" value="without" checked={scheduleMode === 'without'} onChange={e => setScheduleMode(e.target.value)} className="mr-3" />
              <div className="text-sm">Without date time</div>
            </label>

            <label className="inline-flex items-center w-full">
              <input type="radio" name="scheduleMode" value="anyday" checked={scheduleMode === 'anyday'} onChange={e => setScheduleMode(e.target.value)} className="mr-3" />
              <div className="text-sm">Any time on this day</div>
            </label>
            {scheduleMode === 'anyday' && (
              <div className="pl-8">
                <input type="date" value={anyDay} onChange={e => setAnyDay(e.target.value)} className="w-full border px-3 py-2 rounded" disabled={isSubmitting} />
              </div>
            )}
          </div>
        </div>


        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

        <div className="flex items-center justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
          <button
            type="button"
            className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium disabled:opacity-50"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium shadow-sm hover:shadow-md disabled:opacity-50 flex items-center justify-center min-w-[80px]"
            onClick={handleAdd}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddCallList
